import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Brain, Home, Plus, Calendar, Layers, ArrowLeft } from 'lucide-react';
import { mockSessions, stageInfo } from '../mock';

const SessionsList = () => {
  const navigate = useNavigate();
  
  // Safety check - ensure mockSessions is an array
  const sessions = Array.isArray(mockSessions) ? mockSessions : [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-xl flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Idea Fountain</h1>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
            <Button 
              onClick={() => navigate('/session/new')}
              className="bg-gradient-to-r from-violet-600 to-indigo-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Session
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Your Thinking Sessions</h2>
            <p className="text-slate-600">Review and continue your past thinking journeys</p>
          </div>

          {sessions.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Brain className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No sessions yet</h3>
                <p className="text-slate-600 mb-6">Start your first thinking session to see it here</p>
                <Button 
                  onClick={() => navigate('/session/new')}
                  className="bg-gradient-to-r from-violet-600 to-indigo-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Start First Session
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {sessions.map((session) => (
                <Card 
                  key={session.id}
                  className="hover:shadow-lg transition-all duration-300 cursor-pointer group border-2 hover:border-violet-200"
                  onClick={() => navigate(`/session/${session.id}`)}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2 group-hover:text-violet-600 transition-colors">
                          {session.title}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(session.date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric', 
                              year: 'numeric' 
                            })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Layers className="w-4 h-4" />
                            {session.stages?.length || 0} stages completed
                          </span>
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 mb-4">{session.summary}</p>
                    <div className="flex flex-wrap gap-2">
                      {session.stages?.map((stageKey) => {
                        const stage = stageInfo[stageKey];
                        if (!stage) return null;
                        return (
                          <Badge 
                            key={stageKey}
                            variant="secondary"
                            className={`bg-gradient-to-r ${stage.color} text-white border-0`}
                          >
                            {stage.title}
                          </Badge>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionsList;